import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import {
  ArrowLeft,
  ShieldCheck,
  Flame,
  Lock,
  Coin,
  ChartLineUp,
  PaperPlaneTilt,
  EnvelopeSimple,
  ArrowSquareOut,
  Sparkle,
  CheckCircle,
  Brain,
  Stack,
  Trophy,
} from "@phosphor-icons/react";
import ConnectWalletButton from "../components/ConnectWalletButton";
import TokenPricePill from "../components/TokenPricePill";

const CONTRACT = "0x419daCaF319f616d322fC4bB5521A27ce51B8E55";
const PANCAKE = `https://pancakeswap.finance/swap?outputCurrency=${CONTRACT}`;
const BSCSCAN = `https://bscscan.com/token/${CONTRACT}`;
const SNIFFER = `https://tokensniffer.com/token/56/${CONTRACT.toLowerCase()}`;

export default function About() {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <Header />
      <div className="mx-auto max-w-[1100px] px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Back to terminal
        </Link>

        {/* Hero */}
        <section
          data-testid="about-hero"
          className="border border-white/10 p-8 sm:p-12 mb-8 bg-zinc-950/50 relative overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(900px 300px at 70% -10%, rgba(0,216,255,0.12), transparent 60%)",
            }}
          />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-edge mb-3">
              MFSMAI Protocol · Powering MFSMAI Oracle
            </div>
            <h1 className="font-display text-3xl sm:text-5xl font-semibold leading-tight">
              A renounced, no-tax, community-owned token —{" "}
              <span className="text-edge">your key to Pro AI forecasting.</span>
            </h1>
            <p className="mt-5 text-base font-body text-zinc-300 max-w-3xl leading-relaxed">
              MFSMAI is a BEP-20 token on BNB Smart Chain. Hold{" "}
              <span className="text-edge font-semibold">5,000 MFSMAI</span> in
              any wallet to permanently unlock every Pro feature in this
              terminal — AI forecasts, multi-outcome event ranking, watchlists,
              real-time alerts, and edge-based bet deep-links. No subscriptions,
              no payments, no monthly fees.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <ConnectWalletButton variant="hero" />
              <a
                href={PANCAKE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-btn"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-edge/60 px-5 py-2.5 text-sm font-display uppercase tracking-[0.18em] text-zinc-200 hover:bg-white/5 transition-colors"
              >
                <Coin size={14} weight="fill" /> Buy on PancakeSwap
                <ArrowSquareOut size={11} />
              </a>
            </div>
          </div>
        </section>

        {/* Live price card */}
        <section className="mb-8">
          <TokenPricePill variant="card" />
        </section>

        {/* Token facts grid */}
        <section className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-zinc-500 mb-4">
            ◆ Token facts
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <FactCard icon={Coin} label="Total Supply" value="1,000,000" sub="Fixed · No minting" />
            <FactCard icon={ChartLineUp} label="DEX" value="PancakeSwap V2" sub="Liquidity available 24/7" />
            <FactCard icon={Sparkle} label="Buy / Sell Tax" value="4%" sub="2% marketing · 2% operations" />
            <FactCard icon={ShieldCheck} label="Ownership" value="Fully renounced" sub="No admin functions" />
            <FactCard icon={Flame} label="Liquidity" value="Permanently burned" sub="Can't be pulled, ever" />
            <FactCard icon={Lock} label="Contract" value="Verified on BscScan" sub="Audit-ready source code" />
            <FactCard icon={CheckCircle} label="Token Sniffer Score" value="100 / 100" sub="Clean security audit" accent="text-profit" />
            <FactCard icon={Brain} label="Creator Holdings" value="0%" sub="Founder owns no tokens" />
            <FactCard icon={Stack} label="Top 10 Holders" value="9.52%" sub="Highly distributed supply" />
          </div>
        </section>

        {/* Pro tier breakdown */}
        <section className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-white/10 p-6 bg-zinc-950/40">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-zinc-500 mb-3">
              ⬡ Basic — free, no wallet
            </div>
            <h3 className="font-display text-xl font-semibold mb-3">Browse + watch</h3>
            <ul className="space-y-2 text-sm font-body text-zinc-300">
              <BasicCheck>Live Polymarket dashboard + Top Movers</BasicCheck>
              <BasicCheck>Public Track Record + calibration plot</BasicCheck>
              <BasicCheck>AI Edge Leaderboard (read-only)</BasicCheck>
              <BasicCheck>Event browsing (no AI rank)</BasicCheck>
              <BasicCheck>Market detail pages (raw prices)</BasicCheck>
            </ul>
          </div>
          <div
            data-testid="pro-tier-card"
            className="border border-edge/50 p-6 bg-edge/5 relative overflow-hidden"
          >
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-edge/10 blur-2xl" />
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-edge mb-3">
              ◆ Pro — hold 5,000 MFSMAI
            </div>
            <h3 className="font-display text-xl font-semibold mb-3 text-white">Forecast, alert, profit</h3>
            <ul className="space-y-2 text-sm font-body text-zinc-200">
              <ProCheck>MFSMAI Oracle live-streaming AI forecasts</ProCheck>
              <ProCheck>AI ranking across all event outcomes</ProCheck>
              <ProCheck>Personal watchlist + edge-threshold alerts</ProCheck>
              <ProCheck>Web Push notifications (works tab closed)</ProCheck>
              <ProCheck>One-click Polymarket bet deep-links</ProCheck>
              <ProCheck>2-hour background scan covering top markets</ProCheck>
            </ul>
          </div>
        </section>

        {/* Contract + Socials */}
        <section className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-zinc-500 mb-4">
            ◆ Contract &amp; Socials
          </div>
          <div className="border border-white/10 bg-zinc-950/40">
            <ContractRow label="Contract Address" value={CONTRACT} copyable />
            <ContractRow label="Blockchain" value="BNB Smart Chain (BEP-20) · Chain ID 56" />
            <ContractRow label="Decimals" value="18" />
            <LinkRow
              label="View on BscScan"
              href={BSCSCAN}
              icon={ChartLineUp}
              testid="link-bscscan"
            />
            <LinkRow
              label="Token Sniffer audit (100/100)"
              href={SNIFFER}
              icon={ShieldCheck}
              testid="link-sniffer"
            />
            <LinkRow
              label="Buy on PancakeSwap"
              href={PANCAKE}
              icon={Coin}
              testid="link-pancakeswap"
            />
            <LinkRow
              label="Telegram Community"
              href="https://t.me/MFSMAI_COMMUNITY"
              icon={PaperPlaneTilt}
              testid="link-telegram"
            />
            <LinkRow
              label="Email · support@mfsmai.com"
              href="mailto:support@mfsmai.com"
              icon={EnvelopeSimple}
              testid="link-email"
            />
          </div>
        </section>

        {/* Disclaimer */}
        <section className="border-l-2 border-edge/40 pl-4 mb-12">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-zinc-500 mb-2">
            Disclaimer
          </div>
          <p className="text-xs font-body text-zinc-400 leading-relaxed max-w-3xl">
            MFSMAI Oracle is an information terminal. AI forecasts are
            probabilistic estimates produced by the MFSMAI Oracle engine
            and may be
            wrong. Nothing on this site is financial advice. Prediction-market
            trading and token holding carry risk. Always do your own research.
          </p>
        </section>
      </div>
    </div>
  );
}

function FactCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="border border-white/10 p-4 bg-zinc-950/40 hover:border-white/25 transition-colors">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
        <Icon size={13} weight="duotone" className="text-edge" />
        {label}
      </div>
      <div className={`mt-2 font-display text-xl font-semibold tabular-nums ${accent || "text-white"}`}>
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-[11px] font-mono text-zinc-500">{sub}</div>
      )}
    </div>
  );
}

function BasicCheck({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle size={14} className="text-zinc-500 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function ProCheck({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Trophy size={14} weight="fill" className="text-edge mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function ContractRow({ label, value, copyable }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10 last:border-b-0">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 shrink-0">
        {label}
      </div>
      <div className="flex items-center gap-3 min-w-0">
        <code className="font-mono text-xs tabular-nums text-zinc-200 truncate">
          {value}
        </code>
        {copyable && (
          <button
            data-testid="copy-contract-btn"
            onClick={() => {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="text-[10px] font-mono uppercase tracking-widest text-edge hover:text-white border border-edge/40 px-2 py-0.5 shrink-0"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
}

function LinkRow({ label, href, icon: Icon, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10 last:border-b-0 hover:bg-white/5 transition-colors group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={15} weight="duotone" className="text-edge shrink-0" />
        <span className="font-body text-sm text-zinc-200 truncate">{label}</span>
      </div>
      <ArrowSquareOut size={13} className="text-zinc-500 group-hover:text-edge shrink-0" />
    </a>
  );
}
